import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const faqCategories = [
  {
    category: 'Custom Suits',
    icon: 'ri-suit-line',
    faqs: [
      {
        q: 'How long does a custom suit take to make?',
        a: 'A custom suit typically takes 6–8 weeks from your initial consultation to final delivery. This includes fabric selection, measurements, construction, and fitting appointments. Rush orders may be available for an additional fee.',
      },
      {
        q: 'How many fittings are required for a custom suit?',
        a: 'We typically schedule two to three fittings: an initial measurement and design consultation, a basted fitting to check the structure and fit, and a final fitting for any last adjustments before delivery.',
      },
      {
        q: 'What fabrics are available for custom suits?',
        a: 'We carry 5000+ fabrics available for suits including Italian and British wools, cashmere blends, linens, and seasonal fabrics. Our clothiers will help you choose the right fabric for your occasion and climate.',
      },
    ],
  },
  {
    category: 'Custom Shirts',
    icon: 'ri-shirt-line',
    faqs: [
      {
        q: 'How long does a custom shirt take?',
        a: 'Custom shirts typically take 3–4 weeks from your initial fitting to delivery. Once your pattern is on file, reorders are faster.',
      },
      {
        q: 'Can I add a monogram to my custom shirt?',
        a: 'Yes. We offer monogramming on the cuff, chest pocket, or collar in a variety of thread colors and font styles at no additional charge.',
      },
      {
        q: 'What collar and cuff styles are available?',
        a: 'We offer a wide range of collar styles including spread, point, button-down, and cutaway, as well as barrel, French, and convertible cuff options.',
      },
    ],
  },
  {
    category: 'Alterations',
    icon: 'ri-scissors-line',
    faqs: [
      {
        q: 'How long do alterations take?',
        a: 'Most standard alterations are completed within 5–7 business days. Rush service is available upon request for urgent needs.',
      },
      {
        q: 'Do you alter women\'s clothing?',
        a: 'Yes. We alter dresses, gowns, blouses, trousers, and more for both men and women. Bridal alterations are a specialty.',
      },
      {
        q: 'Can you alter designer or luxury garments?',
        a: 'Absolutely. We have extensive experience with high-end designer garments and treat every piece with the utmost care and precision.',
      },
      {
        q: 'How much do alterations cost?',
        a: 'Alteration pricing varies by garment type and complexity. We provide a free estimate at your fitting appointment before any work begins.',
      },
    ],
  },
  {
    category: 'House & Office Calls',
    icon: 'ri-home-line',
    faqs: [
      {
        q: 'Do you offer house or office calls?',
        a: 'Yes. We offer complimentary house and office calls throughout Greater Boston and MetroWest. Simply call or book online to schedule a visit at your preferred location.',
      },
      {
        q: 'Is there an extra charge for house calls?',
        a: 'No. House and office calls are offered at no additional charge for clients in our service area.',
      },
      {
        q: 'What areas do you serve for house calls?',
        a: 'We serve Wellesley, Newton, Needham, Natick, Weston, Brookline, Dedham, Framingham, Boston, and surrounding communities. Call us if you are unsure whether we cover your area.',
      },
    ],
  },
  {
    category: 'General',
    icon: 'ri-question-line',
    faqs: [
      {
        q: 'Where is Custom Clothiers located?',
        a: 'We are located at 326A Washington Street, Wellesley, MA 02481. We are open Monday through Friday 8:00 AM–5:00 PM and Saturday 8:00 AM–4:00 PM.',
      },
      {
        q: 'Do I need an appointment?',
        a: 'Walk-ins are welcome, but we recommend calling ahead or booking online to ensure a dedicated clothier is available for your consultation.',
      },
      {
        q: 'Do you offer gift certificates?',
        a: 'Yes. Gift certificates are available in any denomination and make a wonderful gift for weddings, birthdays, and special occasions. Call us to purchase.',
      },
    ],
  },
];

const FAQPage = () => {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const toggle = (key: string) => {
    setOpenItem(openItem === key ? null : key);
  };

  const allFaqs = faqCategories.flatMap((cat) =>
    cat.faqs.map((faq) => ({ ...faq, category: cat.category }))
  );

  const displayedCategories =
    activeCategory === 'All'
      ? faqCategories
      : faqCategories.filter((c) => c.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">FAQ</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Everything you need to know about custom tailoring, alterations, and working with Custom Clothiers in Wellesley, MA.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b border-gray-200 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setActiveCategory('All')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                activeCategory === 'All'
                  ? 'bg-[#C4A962] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#C4A962] hover:text-[#C4A962]'
              }`}
            >
              All Questions
            </button>
            {faqCategories.map((cat) => (
              <button
                key={cat.category}
                onClick={() => setActiveCategory(cat.category)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                  activeCategory === cat.category
                    ? 'bg-[#C4A962] text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#C4A962] hover:text-[#C4A962]'
                }`}
              >
                {cat.category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {displayedCategories.map((cat) => (
            <div key={cat.category} className="mb-14">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center bg-[#C4A962]/10 rounded-xl">
                  <i className={`${cat.icon} text-[#C4A962] text-xl`}></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{cat.category}</h2>
              </div>
              <div className="space-y-4">
                {cat.faqs.map((faq, idx) => {
                  const key = `${cat.category}-${idx}`;
                  const isOpen = openItem === key;
                  return (
                    <div
                      key={key}
                      className={`border rounded-xl overflow-hidden transition-all ${
                        isOpen ? 'border-[#C4A962] shadow-sm' : 'border-gray-200'
                      }`}
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-colors ${isOpen ? 'bg-[#C4A962]' : 'bg-gray-100'}`}>
                          <i className={`${isOpen ? 'ri-subtract-line text-white' : 'ri-add-line text-gray-500'} text-lg`}></i>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-6 bg-white">
                          <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#C4A962]/10 rounded-2xl mx-auto mb-6">
              <i className="ri-customer-service-2-line text-[#C4A962] text-3xl"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-gray-500 text-lg mb-8 max-w-xl mx-auto">
              Our team is happy to answer any questions you have. Call us, email us, or stop by our Wellesley showroom.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:781-237-3166"
                className="bg-[#C4A962] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#B39952] transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>Call 781-237-3166
              </a>
              <Link
                to="/contact"
                className="border-2 border-gray-200 text-gray-700 px-8 py-4 rounded-lg font-bold hover:border-[#C4A962] hover:text-[#C4A962] transition-colors whitespace-nowrap"
              >
                Send a Message
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
